import type { ComponentPropsWithoutRef, ForwardedRef, ReactNode } from 'react';
import { forwardRef } from 'react';

import classNames from 'classnames';

import styles from './ToggleBox.module.css';

import { visuallyHidden } from '../../styles';
import { Text } from '../Text';
import { Badge } from './Badge/Badge';
import { CheckMark } from './CheckMark';
import { CircleMark } from './CircleMark';

export type ToggleBoxType = 'checkbox' | 'radio';
export type BadgeVariant = 'default' | 'recommended';

interface ToggleBoxBase extends Omit<
  ComponentPropsWithoutRef<'input'>,
  'type'
> {
  label: string;
  type?: ToggleBoxType;
  badge?: string;
  badgeVariant?: BadgeVariant;
  toggleSize?: 'md' | 'lg';
}

export interface ToggleBoxNoSupportCopyChildren extends ToggleBoxBase {
  children?: never;
  supportCopy?: never;
}
export interface ToggleBoxSupportCopy extends ToggleBoxBase {
  supportCopy: string;
  children?: never;
}
export interface ToggleBoxWithChildren extends ToggleBoxBase {
  children: ReactNode;
  supportCopy?: never;
}
export type ToggleBoxProps =
  | ToggleBoxSupportCopy
  | ToggleBoxWithChildren
  | ToggleBoxNoSupportCopyChildren;

function isWithChildren(props: ToggleBoxProps): props is ToggleBoxWithChildren {
  return 'children' in props;
}

function isWithSupportCopy(
  props: ToggleBoxProps
): props is ToggleBoxSupportCopy {
  return 'supportCopy' in props;
}

/**
 * Render a styled toggle switch.
 *
 * @param props - The {@link ToggleBoxProps} to render the checkbox.
 * @cssVariable --tfd-togglebox-badge-padding The padding around the badge
 * @see https://corgi-x.tfd.engineering/components/togglebox
 */
export const ToggleBox = forwardRef(ToggleBox_);

function ToggleBox_(
  props: ToggleBoxProps,
  ref?: ForwardedRef<HTMLInputElement>
) {
  const {
    label,
    badge,
    badgeVariant = 'default',
    className,
    type = 'checkbox',
    ...inputProps
  } = props;

  let supportCopy: string | undefined;
  if (isWithSupportCopy(props)) {
    supportCopy = props.supportCopy;
    delete inputProps.supportCopy;
  }

  let children: ReactNode | undefined;
  if (isWithChildren(props)) {
    children = props.children;
    delete inputProps.children;
  }

  const cssClass = classNames(
    styles.root,
    className,
    styles[`badge-${badgeVariant}`]
  );
  return (
    <label className={cssClass}>
      <input
        ref={ref}
        checked={inputProps.checked}
        className={classNames(visuallyHidden, styles.input)}
        type={type}
        {...inputProps}
      />
      {badge && <Badge text={badge} />}
      <div className={styles.container}>
        <div className={styles.grid}>
          <div>
            <Text
              as="div"
              className={styles.label}
              variant="heading-22"
              color="Kale3"
            >
              <strong>{label}</strong>
            </Text>
            {supportCopy ? (
              <Text as="div" className={styles.supportCopy}>
                {supportCopy}
              </Text>
            ) : (
              children
            )}
          </div>
          <div>
            {type === 'checkbox' && (
              <CheckMark
                disabled={inputProps.disabled}
                checked={inputProps.checked}
              />
            )}
            {type === 'radio' && (
              <CircleMark
                disabled={inputProps.disabled}
                checked={inputProps.checked}
                size={props.toggleSize}
              />
            )}
          </div>
        </div>
      </div>
    </label>
  );
}
